/**
 * @file BillStockDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-14
 * @modified 2026-10-14
 * @description A bill's PO Reference and Stock flag (Cam, 2026-10-14),
 *              editable on ANY bill — approved, paid or keyed in Xero
 *              (PATCH /api/bills/:id/local-fields). Both are this system's
 *              own: never sent to Xero, never refused because the bill is in
 *              Xero. Audited.
 */

import React, { useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import { useSlideOutButton } from './SlideOutButton';
import './BillLocalEdits.css';

const PO_REFERENCE_MAX = 100;

function StockBody({ poReference, setPoReference, isStock, setIsStock, error, saving }) {
  useSlideOutButton([saving]);
  return (
    <div className="bill-local-body">
      <p className="bill-local-hint">Kept in this system only — never sent to Xero.</p>
      <div className="bill-local-field">
        <label htmlFor="bill-local-po-reference">PO Reference</label>
        <Input
          id="bill-local-po-reference"
          type="text"
          value={poReference}
          onChange={(e) => setPoReference(e.target.value.slice(0, PO_REFERENCE_MAX))}
          disabled={saving}
          placeholder="A PO number the invoice quoted, not linked"
          autoFocus
        />
      </div>
      <div className="bill-local-field bill-local-toggle">
        <label htmlFor="bill-local-is-stock">Stock — goods bought for our own stock, not for a customer order</label>
        <div className="checkbox-container">
          <div className="check light">
            <input id="bill-local-is-stock" type="checkbox" checked={isStock} disabled={saving}
                   onChange={(e) => setIsStock(e.target.checked)} />
            <label htmlFor="bill-local-is-stock"></label>
          </div>
        </div>
      </div>
      {error && <div className="bill-local-error" role="alert">{error}</div>}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.bill — id, po_reference, is_stock, supplier_reference, bill_number
 * @param {function} props.onClose
 * @param {function} props.onSaved — ({ po_reference, is_stock }) after they were saved
 */
function BillStockDialog({ bill, onClose, onSaved }) {
  const [poReference, setPoReference] = useState(bill?.po_reference || '');
  const [isStock, setIsStock] = useState(!!bill?.is_stock);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  if (!bill) return null;
  const unchanged = poReference.trim() === (bill.po_reference || '').trim() && isStock === !!bill.is_stock;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bills/${bill.id}/local-fields`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ po_reference: poReference.trim() || null, is_stock: isStock }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.error || 'They could not be saved.');
        return;
      }
      onSaved && onSaved({ po_reference: data.po_reference ?? null, is_stock: !!data.is_stock });
      onClose && onClose();
    } catch (err) {
      setError(`They could not be saved: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const ref = bill.supplier_reference || bill.bill_number || `#${bill.id}`;
  return (
    <Modal
      isOpen
      onClose={saving ? () => {} : onClose}
      title={`PO Reference and Stock — bill ${ref}`}
      size="md"
      className="bill-local"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" data-write-action onClick={save} disabled={saving || unchanged}
                    title={unchanged ? 'Nothing has changed' : 'Save the PO Reference and Stock'}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${saving ? ' spinning' : ''}`}>{saving ? 'progress_activity' : 'save'}</span>
              </div>
              <span className="btn-label">{saving ? 'Saving…' : 'Save'}</span>
            </button>
          </div>
        </div>
      }
    >
      <StockBody poReference={poReference} setPoReference={setPoReference} isStock={isStock} setIsStock={setIsStock}
                 error={error} saving={saving} />
    </Modal>
  );
}

export default BillStockDialog;
